/*
  component-glossee-announcement-bar.js — Glossée Duyuru Çubuğu (Phase 3.0)
  Mobile-only (< 990px) rotation for the announcement bar: one item at a time, autoplay (setting),
  prev / next buttons, horizontal swipe, and the Theme Editor block selection.
  Loaded by the section only when it renders more than one item; on desktop the CSS shows every item and
  this element does nothing except keep the active item ready for a resize back to mobile.
  Instance-safe: every <glossee-announcement-bar> keeps its own state (several bars can coexist).
  Motion: 150ms fade + 8px vertical slide (out upwards, in from below), none under prefers-reduced-motion,
  and autoplay is off under prefers-reduced-motion, while the tab is hidden, while hovered / focused
  and while a block is selected in the Theme Editor.
*/
(function () {
  'use strict';
  if (customElements.get('glossee-announcement-bar')) return;

  var MOBILE = '(max-width: 989px)';
  var FADE_MS = 150;
  var SWIPE_PX = 40;
  var MIN_INTERVAL_MS = 3000;

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* resolves with the Animation (or null) once it has finished; `fill` = 'forwards' keeps the end state
     until the caller cancels it, so the leaving item never flashes back before it is hidden */
  function animate(el, keyframes, fill) {
    return new Promise(function (resolve) {
      if (typeof el.animate !== 'function') { resolve(null); return; }
      var done = false;
      var anim = el.animate(keyframes, { duration: FADE_MS, easing: 'ease-in-out', fill: fill || 'none' });
      function finish() { if (done) return; done = true; resolve(anim); }
      anim.onfinish = finish;
      anim.oncancel = finish;
      if (anim.finished && typeof anim.finished.then === 'function') anim.finished.then(finish, finish);
      setTimeout(finish, FADE_MS + 100); // safety: a frozen/hidden tab never blocks the rotation
    });
  }

  class GlosseeAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-glossee-announcement-item]'));
      if (this.items.length < 2) return;

      this.mq = window.matchMedia(MOBILE);
      this.list = this.querySelector('[data-glossee-announcement-list]');
      this.autoplay = this.getAttribute('data-autoplay') === 'true';
      var seconds = parseFloat(this.getAttribute('data-interval'));
      this.interval = Math.max(MIN_INTERVAL_MS, (isNaN(seconds) ? 5 : seconds) * 1000);

      this.index = 0;
      for (var i = 0; i < this.items.length; i++) {
        if (this.items[i].classList.contains('is-active')) { this.index = i; break; }
      }
      this.timer = null;
      this.busy = false;
      this.token = 0;
      this.hovered = false;
      this.focused = false;
      this.editorHold = false;
      this.pointerStart = null;

      this.applyActive();
      this.bind();
      this.sync();
    }

    disconnectedCallback() {
      this.stop();
      if (this.unbind) this.unbind();
    }

    /* ------------------------------------------------------------------ state */
    isMobile() { return this.mq.matches; }

    applyActive() {
      var self = this;
      this.items.forEach(function (item, i) {
        item.classList.toggle('is-active', i === self.index);
      });
    }

    sync() {
      var run = this.isMobile() && this.autoplay && !reducedMotion() && !document.hidden &&
        !this.hovered && !this.focused && !this.editorHold;
      if (run) this.start(); else this.stop();
    }

    start() {
      var self = this;
      this.stop();
      this.timer = setTimeout(function () {
        self.timer = null;
        self.go(1).then(function () { self.sync(); });
      }, this.interval);
    }

    stop() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
    }

    go(direction) {
      var n = this.items.length;
      return this.show((this.index + direction + n) % n);
    }

    show(next) {
      var self = this;
      var current = this.items[this.index];
      var target = this.items[next];
      if (this.busy || !target || target === current) return Promise.resolve();
      this.busy = true;
      var token = ++this.token;

      var motion = this.isMobile() && !reducedMotion();
      var leave = motion
        ? animate(current, [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-8px)' }], 'forwards')
        : Promise.resolve(null);

      return leave.then(function (anim) {
        if (anim) anim.cancel();
        if (token !== self.token) return null; // superseded (Theme Editor block selection)
        self.index = next;
        self.applyActive();
        return motion
          ? animate(target, [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }])
          : null;
      }).then(function () {
        if (token === self.token) self.busy = false;
      });
    }

    /* user action: switch now and restart the autoplay timing */
    step(direction) {
      var self = this;
      this.stop();
      this.go(direction).then(function () { self.sync(); });
    }

    /* ------------------------------------------------------------------ events */
    bind() {
      var self = this;
      var handlers = [];
      function on(target, type, fn, opts) {
        target.addEventListener(type, fn, opts);
        handlers.push(function () { target.removeEventListener(type, fn, opts); });
      }

      var prev = this.querySelector('[data-glossee-announcement-prev]');
      var next = this.querySelector('[data-glossee-announcement-next]');
      if (prev) on(prev, 'click', function () { self.step(-1); });
      if (next) on(next, 'click', function () { self.step(1); });

      // swipe (touch / pen / mouse drag) on the item area; vertical page scrolling stays native (touch-action: pan-y)
      if (this.list) {
        on(this.list, 'pointerdown', function (event) {
          if (!self.isMobile() || event.button > 0) return;
          self.pointerStart = { x: event.clientX, y: event.clientY };
        });
        on(this.list, 'pointerup', function (event) {
          var start = self.pointerStart;
          self.pointerStart = null;
          if (!start || !self.isMobile()) return;
          var dx = event.clientX - start.x;
          var dy = event.clientY - start.y;
          if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy)) return;
          // a swipe must not also trigger the item link
          var swallow = function (e) { e.preventDefault(); e.stopPropagation(); };
          self.list.addEventListener('click', swallow, { capture: true, once: true });
          setTimeout(function () { self.list.removeEventListener('click', swallow, { capture: true }); }, 300);
          self.step(dx < 0 ? 1 : -1);
        });
        on(this.list, 'pointercancel', function () { self.pointerStart = null; });
      }

      // pause while hovered (mouse) or focused (keyboard / assistive tech)
      on(this, 'pointerenter', function (event) {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        self.hovered = true; self.sync();
      });
      on(this, 'pointerleave', function (event) {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        self.hovered = false; self.sync();
      });
      on(this, 'focusin', function () { self.focused = true; self.sync(); });
      on(this, 'focusout', function (event) {
        if (event.relatedTarget && self.contains(event.relatedTarget)) return;
        self.focused = false; self.sync();
      });

      on(document, 'visibilitychange', function () { self.sync(); });

      var onMq = function () { self.applyActive(); self.sync(); };
      if (typeof this.mq.addEventListener === 'function') on(this.mq, 'change', onMq);
      else if (typeof this.mq.addListener === 'function') { this.mq.addListener(onMq); handlers.push(function () { self.mq.removeListener(onMq); }); }

      // Theme Editor: show the selected block and hold the rotation while it is selected
      on(document, 'shopify:block:select', function (event) {
        if (!event.target || !event.target.contains(self)) return;
        var id = event.detail && event.detail.blockId;
        var i = -1;
        self.items.forEach(function (item, k) { if (item.getAttribute('data-block-id') === id) i = k; });
        self.editorHold = true;
        self.stop();
        if (i > -1 && i !== self.index) {
          self.token++; // drop any in-flight rotation
          self.busy = false;
          self.index = i;
          self.applyActive();
        }
      });
      on(document, 'shopify:block:deselect', function (event) {
        if (!event.target || !event.target.contains(self)) return;
        self.editorHold = false;
        self.sync();
      });

      this.unbind = function () { handlers.forEach(function (off) { off(); }); };
    }
  }

  customElements.define('glossee-announcement-bar', GlosseeAnnouncementBar);
})();
